import { toast } from "sonner";

/**
 * Thin convenience wrapper around `sonner`'s `toast` with a mandatory Undo
 * action. Centralizes label, duration, and success/error follow-up behavior
 * so every non-movement mutation in the graph editor (and other surfaces)
 * can reach for a single helper instead of re-implementing the pattern.
 *
 * Behavior:
 *  - Displays the `message` as a `toast(...)` with a 6s default duration.
 *  - Shows an "Undo" action button bound to `onUndo`.
 *  - If `onUndo` throws or rejects, shows `undoErrorMessage` as an error toast.
 *  - If `onUndo` resolves, shows `undoSuccessMessage` as a success toast
 *    (both messages are optional — omit to keep the UI quiet).
 *
 * Usage:
 * ```ts
 * toastUndo({
 *   message: formatGraphMessage(locale, "removeInfluenceToast", {...}),
 *   undoLabel: graphMessage(locale, "undo"),
 *   onUndo: async () => graphCollectionsApi.restoreInfluence(collectionId, id, {
 *     deactivatedCommentIds,
 *   }),
 *   undoSuccessMessage: graphMessage(locale, "restoreSucceededToast"),
 *   undoErrorMessage: graphMessage(locale, "restoreFailedToast"),
 * });
 * ```
 */
export interface ToastUndoOptions {
  message: string;
  undoLabel: string;
  onUndo: () => Promise<void> | void;
  durationMs?: number;
  /** Optional success toast shown after a successful undo. */
  undoSuccessMessage?: string;
  /** Optional error toast shown if the undo throws. Defaults to the message. */
  undoErrorMessage?: string;
  /**
   * When provided, called on undo success so the caller can trigger a
   * downstream refetch / invalidation without awaiting inside onUndo.
   */
  onUndoSuccess?: () => void;
  /**
   * When provided, called on undo failure.
   */
  onUndoError?: (error: unknown) => void;
}

export const toastUndo = ({
  message,
  undoLabel,
  onUndo,
  durationMs = 6000,
  undoSuccessMessage,
  undoErrorMessage,
  onUndoSuccess,
  onUndoError,
}: ToastUndoOptions): void => {
  toast(message, {
    duration: durationMs,
    action: {
      label: undoLabel,
      onClick: async () => {
        try {
          await onUndo();
          if (onUndoSuccess) {
            onUndoSuccess();
          }
          if (undoSuccessMessage) {
            toast.success(undoSuccessMessage);
          }
        } catch (error) {
          if (onUndoError) {
            onUndoError(error);
          }
          if (undoErrorMessage) {
            toast.error(undoErrorMessage);
          }
        }
      },
    },
  });
};

/**
 * Variant that surfaces a success-tinted toast (checkmark) with the same
 * Undo pattern. Useful for add-style actions where the primary toast is a
 * success message ("Influence added") but the user may still want to undo.
 */
export const toastSuccessWithUndo = ({
  message,
  undoLabel,
  onUndo,
  durationMs = 6000,
  undoSuccessMessage,
  undoErrorMessage,
  onUndoSuccess,
  onUndoError,
}: ToastUndoOptions): void => {
  toast.success(message, {
    duration: durationMs,
    action: {
      label: undoLabel,
      onClick: async () => {
        try {
          await onUndo();
          if (onUndoSuccess) {
            onUndoSuccess();
          }
          if (undoSuccessMessage) {
            toast.success(undoSuccessMessage);
          }
        } catch (error) {
          if (onUndoError) {
            onUndoError(error);
          }
          if (undoErrorMessage) {
            toast.error(undoErrorMessage);
          }
        }
      },
    },
  });
};
